import { Controller } from "@hotwired/stimulus";
import {
  getChartConfig,
  generateDatasetColors,
  updateChartTheme,
} from "../../utils/chart_config";
import {
  ChartDataConverter,
  TooltipCallbacks,
} from "../../utils/chart_data_formatters";
import {
  errorHandler,
  withGracefulDegradation,
} from "../../utils/error_handler";
import { chartPerformanceOptimizer } from "../../utils/chart_performance";

// Connects to data-controller="analytics--chart"
export default class extends Controller {
  static targets = ["canvas", "error", "loading"];
  static values = {
    type: String,
    data: Object,
    options: Object,
    drillDown: String,
    sectionType: String,
    formatCurrency: Boolean,
    formatPercentage: Boolean,
    fromCents: Boolean,
    dateFormat: String,
    labelKey: String,
    valueKey: String,
  };

  async connect() {
    if (this.isConnected) {
      return;
    }
    this.isConnected = true;

    if (!this.checkBrowserCompatibility()) {
      return;
    }

    this.setupEventListeners();
    this.setupConnectionListeners();

    // Lazy load Chart.js if not already available
    if (!window.Chart) {
      this.showLoading();
      try {
        await window.loadChartJs();
      } catch (error) {
        console.error("Failed to load Chart.js:", error);
        this.showError("Failed to load charting library");
        return;
      }
    }

    // Use performance optimizer for chart initialization
    const priority = this.element.dataset.priority || "normal";
    chartPerformanceOptimizer.queueChart(this, priority);

    this.setupThemeWatcher();
  }

  disconnect() {
    this.cleanup();
    document.removeEventListener(
      "analytics:connectionRestored",
      this.handleConnectionRestored.bind(this),
    );
    document.removeEventListener(
      "analytics:connectionLost",
      this.handleConnectionLost.bind(this),
    );

    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }

    // Reset state flags
    this.isInitializing = false;
    this.isConnected = false;
  }

  // Set up event listeners for data updates
  setupEventListeners() {
    this.element.addEventListener(
      "analytics:sectionDataUpdated",
      this.handleDataUpdate.bind(this),
    );
    window.addEventListener("resize", this.handleResize.bind(this));
  }

  // Set up theme change watcher
  setupThemeWatcher() {
    // Watch for theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          this.handleThemeChange();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // Also listen for system theme changes
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      mediaQuery.addEventListener("change", this.handleThemeChange.bind(this));
    }
  }

  // Clean up event listeners
  cleanup() {
    this.element.removeEventListener(
      "analytics:sectionDataUpdated",
      this.handleDataUpdate.bind(this),
    );
    window.removeEventListener("resize", this.handleResize.bind(this));
  }

  // Initialize Chart.js with full configuration
  initializeChart() {
    const initStartTime = performance.now();

    // Chart.js should already be loaded by connect(), but double-check
    if (!window.Chart) {
      console.error("Chart.js not available");
      this.showError("Failed to load charting library");
      return;
    }

    if (!this.hasCanvasTarget) {
      console.error("Canvas target not found");
      this.showError("Chart display area not available");
      return;
    }

    // Prevent multiple simultaneous initializations
    if (this.isInitializing) {
      return;
    }

    try {
      this.isInitializing = true;
      this.hideError();
      this.showLoading();

      // Destroy existing chart instance if it exists
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }

      // Check if canvas already has a Chart.js instance attached
      // Chart.js stores instances in a global registry
      const existingChart = window.Chart.getChart(this.canvasTarget);
      if (existingChart) {
        existingChart.destroy();
      }

      const ctx = this.getCanvasContext();
      if (!ctx) {
        throw new Error("Unable to get canvas context");
      }

      const configStartTime = performance.now();
      const chartConfig = this.buildChartConfig();
      this.validateChartConfig(chartConfig);
      const configTime = performance.now() - configStartTime;

      const renderStartTime = performance.now();
      this.chart = new window.Chart(ctx, chartConfig);
      const renderTime = performance.now() - renderStartTime;

      this.hideLoading();

      const totalTime = performance.now() - initStartTime;
      if (totalTime > 1000) {
        console.warn(`Chart initialization breakdown: total=${totalTime.toFixed(2)}ms, config=${configTime.toFixed(2)}ms, render=${renderTime.toFixed(2)}ms`);
      }

      // Reset retry counter on successful initialization
      errorHandler.resetRetries(this.element.id);
    } catch (error) {
      this.handleChartInitializationError(error);
    } finally {
      this.isInitializing = false;
    }
  }

  // Build complete Chart.js configuration
  buildChartConfig() {
    const chartData = this.processChartData(
      this.dataValue || { labels: [], datasets: [] },
    );
    const chartType = this.typeValue || "bar";

    // Build tooltip callbacks based on data format
    const tooltipCallbacks = this.buildTooltipCallbacks();

    // Detect large datasets and optimize performance
    const totalDataPoints = chartData.datasets.reduce((sum, dataset) => {
      return sum + (dataset.data?.length || 0);
    }, 0);
    const isLargeDataset = totalDataPoints > 50;

    // Performance optimizations for large datasets
    const performanceOptions = isLargeDataset ? {
      animation: false, // Disable animations for large datasets
      parsing: false, // Disable parsing if data is already formatted
      normalized: true, // Data is already normalized
    } : {};

    // Get base configuration from utility
    const baseConfig = getChartConfig(chartType, chartData, {
      plugins: {
        tooltip: {
          callbacks: {
            ...tooltipCallbacks,
            afterLabel: () => {
              return this.hasDrillDownValue ? "Click to view details" : "";
            },
          },
        },
      },
      onClick: this.handleChartClick.bind(this),
      onHover: this.handleChartHover.bind(this),
      ...performanceOptions,
      ...this.optionsValue,
    });

    return baseConfig;
  }

  // Handle theme changes
  handleThemeChange() {
    if (this.chart) {
      updateChartTheme(this.chart);
    }
  }

  // Build tooltip callbacks based on data format
  buildTooltipCallbacks() {
    if (this.hasFormatCurrencyValue && this.formatCurrencyValue) {
      return TooltipCallbacks.currency(
        this.hasFromCentsValue ? this.fromCentsValue : false,
      );
    } else if (this.hasFormatPercentageValue && this.formatPercentageValue) {
      return TooltipCallbacks.percentage(true);
    } else {
      return TooltipCallbacks.number();
    }
  }

  // Process and format chart data
  processChartData(rawData) {
    const startTime = performance.now();

    if (
      !rawData ||
      (!rawData.datasets &&
        !Array.isArray(rawData) &&
        typeof rawData !== "object")
    ) {
      return { labels: [], datasets: [] };
    }

    // If rawData is already in Chart.js format, just apply colors
    if (rawData.datasets && Array.isArray(rawData.datasets)) {
      const result = this.applyDatasetColors(rawData);
      const processingTime = performance.now() - startTime;
      if (processingTime > 100) {
        console.warn(`Chart data processing took ${processingTime.toFixed(2)}ms (color application only)`);
      }
      return result;
    }

    // Convert Rails data to Chart.js format using data converter
    const chartType = this.typeValue || "bar";
    const conversionOptions = {
      labelKey: this.hasLabelKeyValue ? this.labelKeyValue : "label",
      valueKey: this.hasValueKeyValue ? this.valueKeyValue : "value",
      formatCurrency: this.hasFormatCurrencyValue
        ? this.formatCurrencyValue
        : false,
      formatPercentage: this.hasFormatPercentageValue
        ? this.formatPercentageValue
        : false,
      fromCents: this.hasFromCentsValue ? this.fromCentsValue : false,
      dateFormat: this.hasDateFormatValue ? this.dateFormatValue : "short",
    };

    const conversionStart = performance.now();
    const convertedData = ChartDataConverter.convertToChartData(
      rawData,
      chartType,
      conversionOptions,
    );
    const conversionTime = performance.now() - conversionStart;

    const result = this.applyDatasetColors(convertedData);

    const totalTime = performance.now() - startTime;
    if (totalTime > 100) {
      console.warn(`Chart data processing took ${totalTime.toFixed(2)}ms (conversion: ${conversionTime.toFixed(2)}ms)`);
    }

    return result;
  }

  // Apply consistent colors to datasets
  applyDatasetColors(chartData) {
    if (!chartData.datasets || !Array.isArray(chartData.datasets)) {
      return chartData;
    }

    const chartType = this.typeValue || "bar";
    const isSingleDatasetChart = chartData.datasets.length === 1;
    const requiresMultipleColors = ["pie", "doughnut", "polarArea", "bar"].includes(chartType);

    // For charts with a single dataset, color each data point differently
    // This applies to: pie, doughnut, polarArea, and single-dataset bar charts
    if (isSingleDatasetChart && requiresMultipleColors && chartData.datasets[0]?.data) {
      const dataCount = chartData.datasets[0].data.length;
      const colors = generateDatasetColors(dataCount, "varied");

      const processedData = {
        ...chartData,
        datasets: chartData.datasets.map((dataset) => ({
          ...dataset,
          backgroundColor: colors.map(c => c.backgroundColor),
          borderColor: colors.map(c => c.borderColor),
          hoverBackgroundColor: colors.map(c => c.hoverBackgroundColor),
          hoverBorderColor: colors.map(c => c.hoverBorderColor),
          borderWidth: 2,
        })),
      };

      return processedData;
    }

    // For multi-dataset charts, apply one color per dataset
    const datasetColors = generateDatasetColors(
      chartData.datasets.length,
      "varied",
    );

    const processedData = {
      ...chartData,
      datasets: chartData.datasets.map((dataset, index) => ({
        ...dataset,
        ...datasetColors[index],
        borderWidth: 2,
      })),
    };

    return processedData;
  }

  // Handle chart click events for drill-down functionality
  handleChartClick(_, elements) {
    if (!this.hasDrillDownValue || elements.length === 0) {
      return;
    }

    const element = elements[0];
    const dataIndex = element.index;
    const datasetIndex = element.datasetIndex;

    const clickedData = this.getClickedDataInfo(dataIndex, datasetIndex);

    if (clickedData) {
      this.triggerDrillDown(clickedData);
    }
  }

  // Handle chart hover events
  handleChartHover(_, elements) {
    if (!this.hasCanvasTarget) return;

    if (elements.length > 0 && this.hasDrillDownValue) {
      this.canvasTarget.style.cursor = "pointer";
    } else {
      this.canvasTarget.style.cursor = "default";
    }
  }

  // Get information about clicked data point
  getClickedDataInfo(dataIndex, datasetIndex) {
    if (!this.chart || !this.chart.data) return null;

    const dataset = this.chart.data.datasets[datasetIndex];
    const label = this.chart.data.labels[dataIndex];
    const value = dataset.data[dataIndex];

    return {
      label,
      value,
      datasetLabel: dataset.label,
      drillDownType: this.drillDownValue,
      sectionType: this.sectionTypeValue,
      dataIndex,
      datasetIndex,
    };
  }

  // Trigger drill-down functionality
  triggerDrillDown(clickedData) {
    const drillDownEvent = new CustomEvent("analytics:drillDownRequested", {
      detail: {
        type: clickedData.drillDownType,
        section: clickedData.sectionType,
        filters: this.buildDrillDownFilters(clickedData),
        title: `${clickedData.datasetLabel}: ${clickedData.label}`,
        value: clickedData.value,
      },
      bubbles: true,
    });

    this.element.dispatchEvent(drillDownEvent);
  }

  // Build filters for drill-down based on clicked data
  buildDrillDownFilters(clickedData) {
    const filters = {};

    switch (clickedData.drillDownType) {
      case "party_status":
        filters.status = clickedData.label.toLowerCase();
        break;
      case "financial_category":
        filters.category = clickedData.label;
        break;
      case "guest_engagement":
        filters.engagement_type = clickedData.label;
        break;
      case "timeline_efficiency":
        filters.completion_status = clickedData.label;
        break;
      case "product_performance":
        filters.product_category = clickedData.label;
        break;
      default:
        filters.filter_value = clickedData.label;
    }

    return filters;
  }

  // Handle data updates from dashboard controller
  handleDataUpdate(event) {
    const { sectionType, data } = event.detail;

    if (sectionType === this.sectionTypeValue && data) {
      this.updateChart(data);
    }
  }

  // Update chart with new data
  updateChart(newData) {
    if (!this.chart) {
      this.dataValue = newData;
      this.initializeChart();
      return;
    }

    try {
      this.showLoading();

      const processedData = this.processChartData(newData);
      this.chart.data = processedData;
      this.chart.update("active");

      this.hideLoading();
    } catch (error) {
      console.error("Failed to update chart:", error);
      this.showError("Failed to update chart");
      this.hideLoading();
    }
  }

  // Convert specific data types for different chart sections
  convertSectionData(sectionType, rawData) {
    switch (sectionType) {
      case "party_overview":
        return this.convertPartyOverviewData(rawData);
      case "guest_engagement":
        return this.convertGuestEngagementData(rawData);
      case "financial_insights":
        return this.convertFinancialData(rawData);
      case "timeline_efficiency":
        return this.convertTimelineData(rawData);
      case "product_performance":
        return this.convertProductData(rawData);
      default:
        return ChartDataConverter.convertToChartData(
          rawData,
          this.typeValue || "bar",
        );
    }
  }

  // Convert party overview data
  convertPartyOverviewData(rawData) {
    if (rawData.party_status_breakdown) {
      return ChartDataConverter.convertToPieData(
        rawData.party_status_breakdown,
        {
          labelKey: "status",
          valueKey: "count",
        },
      );
    } else if (rawData.monthly_party_trend) {
      return ChartDataConverter.convertTimeSeriesData(
        rawData.monthly_party_trend,
        {
          dateKey: "month",
          valueKey: "count",
          datasetLabel: "Parties",
          dateFormat: "month",
        },
      );
    }
    return ChartDataConverter.convertToChartData(
      rawData,
      this.typeValue || "bar",
    );
  }

  // Convert guest engagement data
  convertGuestEngagementData(rawData) {
    if (rawData.rsvp_breakdown) {
      return ChartDataConverter.convertToPieData(rawData.rsvp_breakdown, {
        labelKey: "status",
        valueKey: "count",
      });
    }
    return ChartDataConverter.convertToChartData(
      rawData,
      this.typeValue || "bar",
    );
  }

  // Convert financial data
  convertFinancialData(rawData) {
    if (rawData.spending_by_category) {
      return ChartDataConverter.convertToPieData(rawData.spending_by_category, {
        formatCurrency: true,
        fromCents: true,
      });
    } else if (rawData.monthly_spending_trend) {
      return ChartDataConverter.convertTimeSeriesData(
        rawData.monthly_spending_trend,
        {
          dateKey: "month",
          valueKey: "amount",
          datasetLabel: "Spending",
          dateFormat: "month",
          formatCurrency: true,
          fromCents: true,
        },
      );
    }
    return ChartDataConverter.convertToChartData(
      rawData,
      this.typeValue || "bar",
      {
        formatCurrency: true,
        fromCents: true,
      },
    );
  }

  // Convert timeline data
  convertTimelineData(rawData) {
    if (rawData.completion_trend) {
      return ChartDataConverter.convertTimeSeriesData(
        rawData.completion_trend,
        {
          dateKey: "week",
          valueKey: "completed",
          datasetLabel: "Tasks Completed",
          dateFormat: "short",
        },
      );
    } else if (rawData.tasks_by_status) {
      return ChartDataConverter.convertToPieData(rawData.tasks_by_status);
    }
    return ChartDataConverter.convertToChartData(
      rawData,
      this.typeValue || "bar",
    );
  }

  // Convert product data
  convertProductData(rawData) {
    if (rawData.sales_by_category) {
      return ChartDataConverter.convertToPieData(rawData.sales_by_category, {
        formatCurrency: true,
        fromCents: true,
      });
    } else if (rawData.top_products) {
      return ChartDataConverter.convertToBarLineData(rawData.top_products, {
        labelKey: "name",
        valueKey: "revenue",
        datasetLabel: "Revenue",
        formatCurrency: true,
        fromCents: true,
      });
    }
    return ChartDataConverter.convertToChartData(
      rawData,
      this.typeValue || "bar",
      {
        formatCurrency: true,
        fromCents: true,
      },
    );
  }

  // Handle window resize for responsiveness
  handleResize() {
    if (this.chart) {
      this.chart.resize();
    }
  }

  // Show loading state with skeleton
  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden");
    } else {
      // Create skeleton loading if no loading target exists
      this.createSkeletonLoading();
    }

    if (this.hasCanvasTarget) {
      this.canvasTarget.style.opacity = "0.5";
    }
  }

  // Create skeleton loading animation
  createSkeletonLoading() {
    if (this.element.querySelector(".chart-skeleton-loading")) {
      return; // Already exists
    }

    const skeleton = document.createElement("div");
    skeleton.className =
      "chart-skeleton-loading absolute inset-0 bg-surface-alt rounded-lg";
    skeleton.innerHTML = `
      <div class="animate-pulse p-4">
        <div class="h-4 bg-outline rounded w-1/3 mb-4"></div>
        <div class="relative h-48 bg-outline rounded">
          <!-- Animated chart placeholder -->
          <div class="absolute inset-0 flex items-end justify-center space-x-2 p-4">
            <div class="bg-primary opacity-30 rounded-t animate-pulse" style="height: 60%; width: 12px; animation-delay: 0s;"></div>
            <div class="bg-secondary opacity-30 rounded-t animate-pulse" style="height: 80%; width: 12px; animation-delay: 0.2s;"></div>
            <div class="bg-tertiary opacity-30 rounded-t animate-pulse" style="height: 40%; width: 12px; animation-delay: 0.4s;"></div>
            <div class="bg-primary opacity-30 rounded-t animate-pulse" style="height: 70%; width: 12px; animation-delay: 0.6s;"></div>
            <div class="bg-secondary opacity-30 rounded-t animate-pulse" style="height: 50%; width: 12px; animation-delay: 0.8s;"></div>
          </div>
        </div>
      </div>
    `;

    // Make element relative if not already
    if (getComputedStyle(this.element).position === "static") {
      this.element.style.position = "relative";
    }

    this.element.appendChild(skeleton);
  }

  // Hide loading state
  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden");
    }

    // Remove skeleton loading
    const skeleton = this.element.querySelector(".chart-skeleton-loading");
    if (skeleton) {
      skeleton.remove();
    }

    if (this.hasCanvasTarget) {
      this.canvasTarget.style.opacity = "1";
    }
  }

  // Show error state
  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.classList.remove("hidden");
    }
    if (this.hasCanvasTarget) {
      this.canvasTarget.style.display = "none";
    }
  }

  // Hide error state
  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add("hidden");
    }
    if (this.hasCanvasTarget) {
      this.canvasTarget.style.display = "block";
    }
  }

  // Destroy and recreate chart (useful for type changes)
  recreateChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
    this.initializeChart();
  }

  // Export chart as image
  exportChart() {
    if (!this.chart) return;

    try {
      const url = this.chart.toBase64Image();
      const link = document.createElement("a");
      link.download = `chart-${this.sectionTypeValue}-${Date.now()}.png`;
      link.href = url;
      link.click();
    } catch (error) {
      console.error("Failed to export chart:", error);
      this.showError("Unable to export chart");
    }
  }

  // Check browser compatibility for charts
  checkBrowserCompatibility() {
    const compatibility = errorHandler.checkBrowserCompatibility();

    if (!compatibility.isCompatible) {
      const unsupportedFeatures = compatibility.unsupportedFeatures;

      if (unsupportedFeatures.includes("canvas")) {
        errorHandler.handleCompatibilityError("Canvas", this);
        return false;
      }
    }

    return true;
  }

  // Get canvas context with error handling
  getCanvasContext() {
    try {
      const ctx = this.canvasTarget.getContext("2d");
      if (!ctx) {
        throw new Error("Canvas context not available");
      }
      return ctx;
    } catch (error) {
      console.error("Failed to get canvas context:", error);
      errorHandler.handleCompatibilityError("Canvas", this);
      return null;
    }
  }

  // Validate chart configuration
  validateChartConfig(config) {
    if (!config || typeof config !== "object") {
      throw new Error("Invalid chart configuration");
    }

    if (!config.type) {
      throw new Error("Chart type not specified");
    }

    if (!config.data || !config.data.datasets) {
      throw new Error("Chart data not properly formatted");
    }
  }

  // Handle chart initialization errors
  handleChartInitializationError(error) {
    console.error("Failed to initialize chart:", error);

    errorHandler.handleChartError(error, this);
    this.hideLoading();

    // Attempt to show fallback if data is available
    this.showTableFallback();
  }

  // Show tabular fallback when charts fail
  showTableFallback() {
    if (
      !this.dataValue ||
      (!this.dataValue.datasets && !Array.isArray(this.dataValue))
    ) {
      this.showEmptyState("No data available for display");
      return;
    }

    try {
      const tableHtml = this.generateTableFallback(this.dataValue);
      this.showFallbackContent(tableHtml, "table");
    } catch (error) {
      console.error("Failed to generate table fallback:", error);
      this.showEmptyState("Unable to display data in any format");
    }
  }

  // Show simplified view for compatibility issues
  showSimplifiedView() {
    if (!this.dataValue) {
      this.showEmptyState("No data available");
      return;
    }

    try {
      const simplifiedHtml = this.generateSimplifiedView(this.dataValue);
      this.showFallbackContent(simplifiedHtml, "simplified");
    } catch (error) {
      console.error("Failed to generate simplified view:", error);
      this.showEmptyState("Unable to display simplified view");
    }
  }

  // Show empty state with custom message
  showEmptyState(message = "No data available") {
    const emptyHtml = `
      <div class="chart-empty-state text-center py-8">
        <div class="text-4xl mb-3">📊</div>
        <h3 class="font-semibold text-on-surface mb-2">No Chart Data</h3>
        <p class="text-sm text-on-surface-alt">
          ${message}
        </p>
      </div>
    `;
    this.showFallbackContent(emptyHtml, "empty");
  }

  // Show offline mode
  showOfflineMode() {
    const offlineHtml = `
      <div class="chart-offline-state text-center py-8">
        <div class="text-4xl mb-3">📡</div>
        <h3 class="font-semibold text-on-surface mb-2">Offline Mode</h3>
        <p class="text-sm text-on-surface-alt mb-4">
          Charts are not available while offline. Data will be restored when connection returns.
        </p>
        <button type="button"
                class="px-4 py-2 bg-primary text-on-primary rounded-md text-sm font-medium hover:bg-primary-hover transition-colors"
                data-action="click->analytics--chart#checkConnection">
          Check Connection
        </button>
      </div>
    `;
    this.showFallbackContent(offlineHtml, "offline");
  }

  // Generate HTML table from chart data
  generateTableFallback(chartData) {
    const { labels, datasets } = chartData;

    if (!labels || !datasets || datasets.length === 0) {
      return '<p class="text-center text-on-surface-alt">No data available</p>';
    }

    let tableHtml = `
      <div class="chart-fallback-table overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-outline">
              <th class="text-left py-2 px-3">Category</th>
    `;

    datasets.forEach((dataset) => {
      tableHtml += `<th class="text-right py-2 px-3">${dataset.label || "Value"}</th>`;
    });

    tableHtml += `
            </tr>
          </thead>
          <tbody>
    `;

    labels.forEach((label, index) => {
      tableHtml += `<tr class="border-b border-outline-variant">`;
      tableHtml += `<td class="py-2 px-3 font-medium">${label}</td>`;

      datasets.forEach((dataset) => {
        const value = dataset.data[index] || 0;
        const formattedValue = this.formatTableValue(value);
        tableHtml += `<td class="text-right py-2 px-3">${formattedValue}</td>`;
      });

      tableHtml += `</tr>`;
    });

    tableHtml += `
          </tbody>
        </table>
      </div>
    `;

    return tableHtml;
  }

  // Format values for table display
  formatTableValue(value) {
    if (this.hasFormatCurrencyValue && this.formatCurrencyValue) {
      const amount =
        this.hasFromCentsValue && this.fromCentsValue ? value / 100 : value;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(amount);
    } else if (this.hasFormatPercentageValue && this.formatPercentageValue) {
      return `${(value * 100).toFixed(1)}%`;
    } else {
      return value.toLocaleString();
    }
  }

  // Show fallback content
  showFallbackContent(html, type = "table") {
    if (this.hasCanvasTarget) {
      this.canvasTarget.style.display = "none";
    }

    // Create or update fallback container
    let fallbackContainer = this.element.querySelector(".chart-fallback");
    if (!fallbackContainer) {
      fallbackContainer = document.createElement("div");
      fallbackContainer.className =
        "chart-fallback bg-surface-alt rounded-lg p-4";
      this.element.appendChild(fallbackContainer);
    }

    const headerMessages = {
      table: "Chart view unavailable - showing data in table format",
      simplified: "Showing simplified view due to browser limitations",
      empty: "No data to display",
      offline: "Offline - charts unavailable",
    };

    const headerMessage = headerMessages[type] || headerMessages.table;

    fallbackContainer.innerHTML = `
      <div class="text-center mb-3">
        <p class="text-sm text-on-surface-alt">
          ${headerMessage}
        </p>
      </div>
      ${html}
    `;
    fallbackContainer.style.display = "block";
    fallbackContainer.setAttribute("data-fallback-type", type);
  }

  // Retry chart initialization
  retryInitialization() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }

    // Hide any fallback content
    const fallbackContainer = this.element.querySelector(".chart-fallback");
    if (fallbackContainer) {
      fallbackContainer.style.display = "none";
    }

    if (this.hasCanvasTarget) {
      this.canvasTarget.style.display = "block";
    }

    this.initializeChart();
  }

  // Enhanced error display with retry option
  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.innerHTML = `
        <div class="text-center py-6">
          <div class="text-error text-4xl mb-3">⚠️</div>
          <p class="text-sm text-on-surface-alt mb-4">
            ${message}
          </p>
          <div class="space-y-2">
            <button type="button"
                    class="px-4 py-2 bg-button-primary text-on-button-primary rounded-md text-sm font-medium hover:bg-button-primary-pop transition-colors"
                    data-action="click->analytics--chart#retryInitialization">
              Try Again
            </button>
            <button type="button"
                    class="px-4 py-2 bg-surface-alt text-on-surface rounded-md text-sm font-medium hover:bg-surface-alt-hover transition-colors"
                    data-action="click->analytics--chart#showTableFallback">
              Show Data Table
            </button>
          </div>
        </div>
      `;
      this.errorTarget.classList.remove("hidden");
    }

    if (this.hasCanvasTarget) {
      this.canvasTarget.style.display = "none";
    }
  }

  // Generate simplified view for compatibility issues
  generateSimplifiedView(chartData) {
    const { labels, datasets } = chartData;

    if (!labels || !datasets || datasets.length === 0) {
      return '<p class="text-center text-on-surface-alt">No data available</p>';
    }

    // Create a simple card-based layout
    let simplifiedHtml = '<div class="simplified-chart-view grid gap-3">';

    if (datasets.length === 1) {
      // Single dataset - show as cards
      const dataset = datasets[0];
      labels.forEach((label, index) => {
        const value = dataset.data[index] || 0;
        const formattedValue = this.formatTableValue(value);

        simplifiedHtml += `
          <div class="bg-surface rounded-lg p-3 border border-outline">
            <div class="text-sm font-medium text-on-surface">${label}</div>
            <div class="text-lg font-bold text-on-primary">${formattedValue}</div>
          </div>
        `;
      });
    } else {
      // Multiple datasets - show as grouped cards
      labels.forEach((label, index) => {
        simplifiedHtml += `
          <div class="bg-surface rounded-lg p-3 border border-outline">
            <div class="text-sm font-medium text-on-surface mb-2">${label}</div>
            <div class="space-y-1">
        `;

        datasets.forEach((dataset) => {
          const value = dataset.data[index] || 0;
          const formattedValue = this.formatTableValue(value);

          simplifiedHtml += `
            <div class="flex justify-between text-xs">
              <span class="text-on-surface-alt">${dataset.label || "Value"}</span>
              <span class="font-medium">${formattedValue}</span>
            </div>
          `;
        });

        simplifiedHtml += "</div></div>";
      });
    }

    simplifiedHtml += "</div>";
    return simplifiedHtml;
  }

  // Check connection status
  checkConnection() {
    if (navigator.onLine) {
      // Connection restored, try to reinitialize
      this.retryInitialization();
    } else {
      // Still offline
      this.showError("Still offline. Please check your internet connection.");
    }
  }

  // Handle connection restored event
  handleConnectionRestored() {
    // Remove offline fallback and try to reinitialize
    const fallbackContainer = this.element.querySelector(".chart-fallback");
    if (
      fallbackContainer &&
      fallbackContainer.getAttribute("data-fallback-type") === "offline"
    ) {
      this.retryInitialization();
    }
  }

  // Handle connection lost event
  handleConnectionLost() {
    // Show offline mode if chart fails to load
    this.showOfflineMode();
  }

  // Setup connection event listeners
  setupConnectionListeners() {
    document.addEventListener(
      "analytics:connectionRestored",
      this.handleConnectionRestored.bind(this),
    );
    document.addEventListener(
      "analytics:connectionLost",
      this.handleConnectionLost.bind(this),
    );
  }
}
